import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Power, PowerOff, Loader2, TestTube, Tags, Pencil } from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';
import { toast } from 'sonner';

interface EnergyMeter {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  serialPort: string;
  baudRate: number;
  dataBits: number;
  stopBits: number;
  parity: string;
  slaveId: number;
  isActive: boolean;
  registerAddresses?: string;
  createdAt: string;
  tags?: any[];
}

const AVAILABLE_TAGS = [
  {
    group: 'Current (A)',
    tags: [
      { addr: 769, name: 'Phase 1 Current' },
      { addr: 770, name: 'Phase 2 Current' },
      { addr: 771, name: 'Phase 3 Current' },
      { addr: 772, name: 'Neutral Current' },
      { addr: 773, name: 'Avg Current' },
    ],
  },
  {
    group: 'Voltage (V)',
    tags: [
      { addr: 779, name: 'Voltage V12 (L-L)' },
      { addr: 780, name: 'Voltage V23 (L-L)' },
      { addr: 781, name: 'Voltage V31 (L-L)' },
      { addr: 782, name: 'Avg Voltage (L-L)' },
      { addr: 783, name: 'Voltage V1N (L-N)' },
      { addr: 784, name: 'Voltage V2N (L-N)' },
      { addr: 785, name: 'Voltage V3N (L-N)' },
      { addr: 786, name: 'Avg Voltage (L-N)' },
    ],
  },
  {
    group: 'Power & Frequency',
    tags: [
      { addr: 795, name: 'Total Active Power (kW)' },
      { addr: 803, name: 'Total Reactive Power (kvar)' },
      { addr: 807, name: 'Total Apparent Power (kVA)' },
      { addr: 790, name: 'Power Factor' },
      { addr: 791, name: 'Frequency (Hz)' },
    ],
  },
  {
    group: 'Energy',
    tags: [
      { addr: 1304, name: 'Active Energy Import (kWh)' },
      { addr: 1305, name: 'Active Energy Import Alt 1305 (kWh)' },
      { addr: 1307, name: 'Active Energy Export (kWh)' },
    ],
  },
];

const flattenAvailableTags = () => AVAILABLE_TAGS.flatMap(group => group.tags);

const getUnitForAddress = (address: number) => {
  if ([769, 770, 771, 772, 773, 778].includes(address)) return 'A';
  if ([779, 780, 781, 782, 783, 784, 785, 786].includes(address)) return 'V';
  if (address === 795) return 'kW';
  if (address === 803) return 'kvar';
  if (address === 807) return 'kVA';
  if (address === 790) return '';
  if (address === 791) return 'Hz';
  if ([1304, 1305, 1307].includes(address)) return 'kWh';
  return '';
};

const getTagNameForAddress = (address: number) => {
  const tag = flattenAvailableTags().find((candidate) => candidate.addr === address);
  return tag?.name.replace(/\s+\([^)]*\)$/g, '') || `Register ${address}`;
};

const buildSelectedTags = (addresses: number[]) => (
  [...new Set(addresses)]
    .filter((address) => Number.isFinite(address))
    .sort((a, b) => a - b)
    .map((address) => ({
      name: getTagNameForAddress(address),
      address,
      unit: getUnitForAddress(address),
    }))
);

const COMMON_TAG_UNITS = [
  { group: 'Electrical', units: ['V', 'mV', 'A', 'mA', 'kW', 'MW', 'kWh', 'MWh', 'kVA', 'kVAR', 'Hz', 'PF', 'Ω'] },
  { group: 'Temperature', units: ['°C', '°F', 'K'] },
  { group: 'Pressure', units: ['bar', 'mbar', 'psi', 'Pa', 'kPa', 'MPa'] },
  { group: 'Flow', units: ['L/min', 'L/h', 'm³/h', 'GPM'] },
  { group: 'Speed', units: ['RPM', 'm/s', 'km/h'] },
  { group: 'Level / Length', units: ['mm', 'cm', 'm', '%'] },
  { group: 'Weight', units: ['g', 'kg', 't'] },
  { group: 'Time', units: ['ms', 's', 'min', 'h'] },
];
const ALL_COMMON_UNITS = COMMON_TAG_UNITS.flatMap(g => g.units);

export function EnergyMetersTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [energyMeters, setEnergyMeters] = useState<EnergyMeter[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [editingMeter, setEditingMeter] = useState<EnergyMeter | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serialPort: 'COM3',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'even',
    slaveId: 1,
    selectedAddresses: [] as number[],
    isActive: true,
  });

  // Tag management state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedMeterForTags, setSelectedMeterForTags] = useState<EnergyMeter | null>(null);
  const [meterTags, setMeterTags] = useState<any[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [editingTag, setEditingTag] = useState<any | null>(null);
  const [tagForm, setTagForm] = useState({ name: '', address: '', unit: '' });
  const [unitIsCustom, setUnitIsCustom] = useState(false);

  const getSessionToken = () => {
    const session = sessionStorage.get();
    return session?.token || null;
  };

  const parseApiJson = async (response: Response, endpoint: string) => {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const rawText = await response.text();

    if (!rawText) {
      return {} as any;
    }

    if (!contentType.includes('application/json')) {
      const preview = rawText.replace(/\s+/g, ' ').trim().slice(0, 120);
      throw new Error(`Unexpected non-JSON response from ${endpoint} (HTTP ${response.status}). Preview: ${preview}`);
    }

    try {
      return JSON.parse(rawText);
    } catch {
      throw new Error(`Invalid JSON response from ${endpoint} (HTTP ${response.status}).`);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadEnergyMeters(selectedClient);
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      
      const data = await adminApi.getClients(token);
      setClients(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEnergyMeters = async (clientId: string) => {
    try {
      setLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const rawText = await response.text();
      let data: any = {};

      if (rawText) {
        if (!contentType.includes('application/json')) {
          const preview = rawText.replace(/\s+/g, ' ').trim().slice(0, 120);
          throw new Error(`Unexpected non-JSON response for energy meters (HTTP ${response.status}). Preview: ${preview}`);
        }
        try {
          data = JSON.parse(rawText);
        } catch {
          throw new Error(`Invalid JSON while loading energy meters (HTTP ${response.status}).`);
        }
      }

      if (!response.ok) throw new Error(data.error);
      setEnergyMeters(data.meters || []);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClient || !formData.name || !formData.serialPort) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const { selectedAddresses: _sa, ...restFormData } = formData;
      const submitBody = {
        ...restFormData,
        registerAddresses: '',
        tags: [],
      };

      let response;
      if (editingMeter) {
        response = await fetch(
          `${API_BASE}/api/clients/${selectedClient}/energy-meters/${editingMeter.id}`,
          {
            method: 'PUT',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify(submitBody),
          }
        );
      } else {
        response = await fetch(
          `${API_BASE}/api/clients/${selectedClient}/energy-meters`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify(submitBody),
          }
        );
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast.success(editingMeter ? 'Energy meter updated' : 'Energy meter created');
      setDialogOpen(false);
      resetForm();
      loadEnergyMeters(selectedClient);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }

    try {
      setTestingConnection(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const connectivityResponse = await fetch(
        `${API_BASE}/api/admin/clients/${selectedClient}/bridge-connectivity`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const connectivity = await parseApiJson(connectivityResponse, '/api/admin/clients/:clientId/bridge-connectivity');
      if (!connectivityResponse.ok) {
        throw new Error(connectivity.error || 'Failed to check bridge connectivity');
      }

      const onlineBridges = Number(connectivity?.counts?.online_bridges || 0);
      const dataFresh = Boolean(connectivity?.data_flow?.is_data_fresh);
      const freshnessWindowSec = Number(connectivity?.thresholds?.data_freshness_window_seconds || 300);
      const recommendationText = Array.isArray(connectivity?.recommendations)
        ? connectivity.recommendations.join(' ')
        : '';

      if (onlineBridges <= 0) {
        toast.error('Bridge is not online for this client', {
          description: connectivity?.summary || recommendationText || 'Ensure bridge service is running on the client machine.',
          duration: 10000,
        });
        return;
      }

      if (editingMeter?.id) {
        const meterResponse = await fetch(
          `${API_BASE}/api/clients/${selectedClient}/energy-meters/${editingMeter.id}/current`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const meterCurrent = await parseApiJson(meterResponse, '/api/clients/:clientId/energy-meters/:meterId/current');
        if (!meterResponse.ok) {
          throw new Error(meterCurrent.error || 'Failed to fetch meter current values');
        }

        const latestMeterMs = (Array.isArray(meterCurrent?.tags) ? meterCurrent.tags : [])
          .map((tag: any) => Date.parse(String(tag?.lastUpdated || '')))
          .filter((value: number) => Number.isFinite(value))
          .reduce((max: number, value: number) => Math.max(max, value), 0);

        const secondsSinceLatest = latestMeterMs > 0
          ? Math.floor(Math.max(0, Date.now() - latestMeterMs) / 1000)
          : null;

        if (secondsSinceLatest !== null && secondsSinceLatest <= freshnessWindowSec) {
          toast.success('Remote bridge test passed', {
            description: `${editingMeter.name} has fresh data (${secondsSinceLatest}s ago).`,
            duration: 9000,
          });
          return;
        }

        if (dataFresh) {
          toast.info('Bridge is online, but this meter has no fresh readings', {
            description: 'Check COM/baud/parity/slave ID and tag addresses for this meter, then save and retry.',
            duration: 10000,
          });
          return;
        }

        toast.error('Bridge is online, but client data is stale', {
          description: connectivity?.summary || recommendationText || 'No recent data received from this client.',
          duration: 10000,
        });
        return;
      }

      if (dataFresh) {
        toast.success('Remote bridge test passed', {
          description: 'Bridge is online and recent client data is reaching the server.',
          duration: 9000,
        });
      } else {
        toast.info('Bridge is online, but no recent data detected', {
          description: connectivity?.summary || recommendationText || 'Verify device wiring and bridge polling settings.',
          duration: 10000,
        });
      }
    } catch (error: any) {
      toast.error('Remote test failed', {
        description: error.message || 'Could not complete remote bridge test.',
        duration: 9000,
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDelete = async (meterId: string) => {
    if (!selectedClient || !confirm('Delete this energy meter?')) return;

    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(
        `${API_BASE}/api/clients/${selectedClient}/energy-meters/${meterId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Energy meter deleted');
      if (selectedClient) {
        loadEnergyMeters(selectedClient);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleToggleActive = async (meter: EnergyMeter) => {
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(
        `${API_BASE}/api/clients/${selectedClient}/energy-meters/${meter.id}`,
        {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ isActive: !meter.isActive }),
        }
      );

      if (!response.ok) throw new Error('Failed to update');
      toast.success(meter.isActive ? 'Meter deactivated' : 'Meter activated');
      if (selectedClient) {
        loadEnergyMeters(selectedClient);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setEditingMeter(null);
    setFormData({
      name: '',
      description: '',
      serialPort: 'COM3',
      baudRate: 9600,
      dataBits: 8,
      stopBits: 1,
      parity: 'even',
      slaveId: 1,
      selectedAddresses: [],
      isActive: true,
    });
  };

  // ── Tag Management ──────────────────────────────────────────────────────────

  const handleOpenTagDialog = async (meter: EnergyMeter) => {
    setSelectedMeterForTags(meter);
    setEditingTag(null);
    setTagForm({ name: '', address: '', unit: '' });
    setUnitIsCustom(false);
    setTagDialogOpen(true);
    await loadMeterTags(meter.id);
  };

  const loadMeterTags = async (meterId: string) => {
    if (!selectedClient) return;
    try {
      setTagLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const res = await fetch(`${API_BASE}/api/clients/${selectedClient}/energy-meters/${meterId}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMeterTags(data.tags || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTagLoading(false);
    }
  };

  const handleSaveTag = async () => {
    if (!selectedClient || !selectedMeterForTags) return;
    if (!tagForm.name.trim() || !tagForm.address.trim()) {
      toast.error('Tag name and address are required');
      return;
    }
    const parsedAddress = Number.parseInt(tagForm.address, 10);
    if (!Number.isFinite(parsedAddress) || parsedAddress < 0) {
      toast.error('Tag address must be a valid positive number');
      return;
    }
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const body = { name: tagForm.name.trim(), address: parsedAddress, unit: tagForm.unit.trim() };
      let res;
      if (editingTag) {
        res = await fetch(
          `${API_BASE}/api/clients/${selectedClient}/energy-meters/${selectedMeterForTags.id}/tags/${editingTag.id}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) }
        );
      } else {
        res = await fetch(
          `${API_BASE}/api/clients/${selectedClient}/energy-meters/${selectedMeterForTags.id}/tags`,
          { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) }
        );
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success(editingTag ? 'Tag updated' : 'Tag added');
      setEditingTag(null);
      setTagForm({ name: '', address: '', unit: '' });
      setUnitIsCustom(false);
      await loadMeterTags(selectedMeterForTags.id);
      await loadEnergyMeters(selectedClient);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDeleteMeterTag = async (tagId: string) => {
    if (!selectedClient || !selectedMeterForTags || !confirm('Delete this tag?')) return;
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const res = await fetch(
        `${API_BASE}/api/clients/${selectedClient}/energy-meters/${selectedMeterForTags.id}/tags/${tagId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to delete tag');
      toast.success('Tag deleted');
      await loadMeterTags(selectedMeterForTags.id);
      await loadEnergyMeters(selectedClient);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────

  const handleEdit = (meter: EnergyMeter) => {
    setEditingMeter(meter);
    setFormData({
      name: meter.name,
      description: meter.description || '',
      serialPort: meter.serialPort,
      baudRate: meter.baudRate,
      dataBits: meter.dataBits,
      stopBits: meter.stopBits,
      parity: meter.parity,
      slaveId: meter.slaveId,
      selectedAddresses: [],
      isActive: meter.isActive,
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold">Energy Meter Management</h2>
        <p className="text-sm text-muted-foreground">Configure Modbus RTU energy meters for clients</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select Client</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {clients.map((client) => (
              <Button
                key={client.id}
                variant={selectedClient === client.id ? 'default' : 'outline'}
                onClick={() => setSelectedClient(client.id)}
              >
                {client.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedClient && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Energy Meters</CardTitle>
              <CardDescription>Manage Modbus RTU connections</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Meter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl">{editingMeter ? 'Edit' : 'Add'} Energy Meter</DialogTitle>
                  <DialogDescription>
                    Add the meter connection settings and select the Modbus tags this client should monitor.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Meter Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Main Meter"
                    />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>COM Port *</Label>
                      <Input
                        value={formData.serialPort}
                        onChange={(e) => setFormData({ ...formData, serialPort: e.target.value })}
                        placeholder="COM3"
                      />
                    </div>
                    <div>
                      <Label>Baud Rate</Label>
                      <Input
                        type="number"
                        value={formData.baudRate}
                        onChange={(e) => setFormData({ ...formData, baudRate: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Data Bits</Label>
                      <Input
                        type="number"
                        value={formData.dataBits}
                        onChange={(e) => setFormData({ ...formData, dataBits: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Stop Bits</Label>
                      <Input
                        type="number"
                        value={formData.stopBits}
                        onChange={(e) => setFormData({ ...formData, stopBits: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Slave ID</Label>
                      <Input
                        type="number"
                        value={formData.slaveId}
                        onChange={(e) => setFormData({ ...formData, slaveId: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Parity</Label>
                    <select
                      value={formData.parity}
                      onChange={(e) => setFormData({ ...formData, parity: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                    >
                      <option value="none">None</option>
                      <option value="even">Even</option>
                      <option value="odd">Odd</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                    <Label>Active</Label>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                  >
                    {testingConnection ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <TestTube className="w-4 h-4 mr-2" />
                        Test Remote Connection
                      </>
                    )}
                  </Button>

                  <Button type="submit" className="w-full">
                    {editingMeter ? 'Update' : 'Create'} Meter
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : energyMeters.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No energy meters configured yet
              </div>
            ) : (
              <div className="space-y-3">
                {energyMeters.map((meter) => (
                  <motion.div
                    key={meter.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-5 border rounded-lg"
                  >
                    <div>
                      <p className="text-base sm:text-lg font-semibold">{meter.name}</p>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        {meter.serialPort} @ {meter.baudRate} baud | Slave ID: {meter.slaveId}
                      </p>
                      {meter.description && (
                        <p className="text-sm sm:text-base">{meter.description}</p>
                      )}
                      {meter.tags && meter.tags.length > 0 && (
                        <p className="text-sm text-muted-foreground mt-1.5">
                          Tags: {meter.tags.slice(0, 5).map((tag: any) => tag.name).join(', ')}
                          {meter.tags.length > 5 ? ` +${meter.tags.length - 5} more` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={meter.isActive ? 'default' : 'secondary'}
                        onClick={() => handleToggleActive(meter)}
                      >
                        {meter.isActive ? (
                          <>
                            <Power className="w-4 h-4 mr-1" />
                            On
                          </>
                        ) : (
                          <>
                            <PowerOff className="w-4 h-4 mr-1" />
                            Off
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(meter)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenTagDialog(meter)}
                        title="Manage Tags"
                      >
                        <Tags className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(meter.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tag Management Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={(open) => { setTagDialogOpen(open); if (!open) { setEditingTag(null); setTagForm({ name: '', address: '', unit: '' }); setUnitIsCustom(false); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">Tags - {selectedMeterForTags?.name}</DialogTitle>
            <DialogDescription>
              Add or manage Modbus register tags for this energy meter. Enter the register address, a display name, and optional unit.
            </DialogDescription>
          </DialogHeader>

          {/* Add / Edit form */}
          <div className="space-y-3 border rounded-md p-4 bg-muted/20">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {editingTag ? 'Edit Tag' : 'Add Tag'}
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-sm">Address *</Label>
                <Input
                  type="number"
                  placeholder="e.g. 1304"
                  value={tagForm.address}
                  onChange={(e) => {
                    const addr = parseInt(e.target.value);
                    const autoUnit = !isNaN(addr) ? getUnitForAddress(addr) : '';
                    const autoName = !isNaN(addr) ? getTagNameForAddress(addr) : '';
                    setTagForm({
                      address: e.target.value,
                      unit: tagForm.unit || autoUnit,
                      name: tagForm.name || autoName,
                    });
                    if (autoUnit && !ALL_COMMON_UNITS.includes(autoUnit)) {
                      setUnitIsCustom(true);
                    } else if (autoUnit) {
                      setUnitIsCustom(false);
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-sm">Name *</Label>
                <Input
                  placeholder="e.g. Phase 1 Current"
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm">Unit</Label>
                <div className="flex flex-col gap-2 mt-1.5">
                  <Select
                    value={unitIsCustom ? '__custom__' : (tagForm.unit || '__none__')}
                    onValueChange={(val) => {
                      if (val === '__custom__') {
                        setUnitIsCustom(true);
                        setTagForm({ ...tagForm, unit: '' });
                      } else if (val === '__none__') {
                        setUnitIsCustom(false);
                        setTagForm({ ...tagForm, unit: '' });
                      } else {
                        setUnitIsCustom(false);
                        setTagForm({ ...tagForm, unit: val });
                      }
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select unit..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {COMMON_TAG_UNITS.map(group => (
                        <div key={group.group}>
                          <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground uppercase tracking-wide">{group.group}</div>
                          {group.units.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </div>
                      ))}
                      <SelectItem value="__custom__">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {unitIsCustom && (
                    <Input
                      placeholder="e.g. kWh"
                      value={tagForm.unit}
                      onChange={(e) => setTagForm({ ...tagForm, unit: e.target.value })}
                      autoFocus
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveTag} className="flex-1">
                {editingTag ? 'Update Tag' : 'Add Tag'}
              </Button>
              {editingTag && (
                <Button size="sm" variant="outline" onClick={() => { setEditingTag(null); setTagForm({ name: '', address: '', unit: '' }); setUnitIsCustom(false); }}>
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Existing tags list */}
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {tagLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : meterTags.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No tags yet. Add one above.</p>
            ) : (
              meterTags.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between px-3 py-2 border rounded-md">
                  <div className="min-w-0">
                    <span className="font-semibold text-base">{tag.name}</span>
                    <span className="text-sm text-muted-foreground ml-2 font-mono">#{tag.address}</span>
                    {tag.unit && <span className="text-sm text-muted-foreground ml-1">({tag.unit})</span>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { 
                        setEditingTag(tag); 
                        setTagForm({ name: tag.name, address: String(tag.address), unit: tag.unit || '' }); 
                        setUnitIsCustom(tag.unit && !ALL_COMMON_UNITS.includes(tag.unit));
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteMeterTag(tag.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
